import React, { useEffect, useRef } from "react";
import { useVeltClient } from "../context";
var LiveStateDataConfig = /** @class */ (function () {
    function LiveStateDataConfig() {
    }
    return LiveStateDataConfig;
}());
export function useLiveStateSyncUtils() {
    var _a = React.useState(), liveStateSyncElement = _a[0], setLiveStateSyncElement = _a[1];
    var client = useVeltClient().client;
    React.useEffect(function () {
        if (!client || liveStateSyncElement)
            return;
        var loadedLiveStateSyncElement = client.getLiveStateSyncElement();
        setLiveStateSyncElement(loadedLiveStateSyncElement);
    }, [client, setLiveStateSyncElement, liveStateSyncElement]);
    return liveStateSyncElement;
}
export function useLiveStateData(liveStateDataId, liveStateDataConfig) {
    var liveStateSyncElement = useLiveStateSyncUtils();
    var _a = React.useState(), data = _a[0], setData = _a[1];
    var subscriptionRef = React.useRef();
    // Memoize inputs
    var inputs = React.useMemo(function () {
        return { liveStateDataId: liveStateDataId, liveStateDataConfig: liveStateDataConfig };
    }, [liveStateDataId, JSON.stringify(liveStateDataConfig)]);
    useEffect(function () {
        if (!(liveStateSyncElement === null || liveStateSyncElement === void 0 ? void 0 : liveStateSyncElement.getLiveStateData))
            return;
        if (subscriptionRef.current) {
            subscriptionRef.current.unsubscribe();
        }
        var subscription = liveStateSyncElement.getLiveStateData(inputs.liveStateDataId, inputs.liveStateDataConfig)
            .subscribe(function (res) {
            setData(res);
        });
        subscriptionRef.current = subscription;
        return function () {
            if (subscriptionRef.current) {
                subscriptionRef.current.unsubscribe();
            }
        };
    }, [liveStateSyncElement === null || liveStateSyncElement === void 0 ? void 0 : liveStateSyncElement.getLiveStateData, inputs]);
    return data;
}
export function useSetLiveStateData(liveStateDataId, liveStateData, config) {
    var liveStateSyncElement = useLiveStateSyncUtils();
    // Memoize inputs
    var inputs = React.useMemo(function () {
        return { liveStateDataId: liveStateDataId, liveStateData: liveStateData, config: config };
    }, [liveStateDataId, JSON.stringify(liveStateData), JSON.stringify(config)]);
    React.useEffect(function () {
        if (!(liveStateSyncElement === null || liveStateSyncElement === void 0 ? void 0 : liveStateSyncElement.setLiveStateData))
            return;
        if (inputs.liveStateDataId && inputs.liveStateData) {
            liveStateSyncElement.setLiveStateData(inputs.liveStateDataId, inputs.liveStateData, inputs.config);
        }
    }, [liveStateSyncElement === null || liveStateSyncElement === void 0 ? void 0 : liveStateSyncElement.setLiveStateData, inputs]);
}
export function useUserEditorState() {
    var liveStateSyncElement = useLiveStateSyncUtils();
    var _a = React.useState(null), data = _a[0], setData = _a[1];
    useEffect(function () {
        if (!(liveStateSyncElement === null || liveStateSyncElement === void 0 ? void 0 : liveStateSyncElement.isUserEditor))
            return;
        var subscription = liveStateSyncElement.isUserEditor().subscribe(function (res) {
            setData(res);
        });
        return function () {
            subscription.unsubscribe();
        };
    }, [liveStateSyncElement === null || liveStateSyncElement === void 0 ? void 0 : liveStateSyncElement.isUserEditor]);
    return data;
}
export function useEditor() {
    var liveStateSyncElement = useLiveStateSyncUtils();
    var _a = React.useState(null), data = _a[0], setData = _a[1];
    useEffect(function () {
        if (!(liveStateSyncElement === null || liveStateSyncElement === void 0 ? void 0 : liveStateSyncElement.getEditor))
            return;
        var subscription = liveStateSyncElement.getEditor().subscribe(function (res) {
            setData(res);
        });
        return function () {
            subscription.unsubscribe();
        };
    }, [liveStateSyncElement === null || liveStateSyncElement === void 0 ? void 0 : liveStateSyncElement.getEditor]);
    return data;
}
export function useEditorAccessTimer() {
    var liveStateSyncElement = useLiveStateSyncUtils();
    var _a = React.useState({ state: 'idle' }), data = _a[0], setData = _a[1];
    useEffect(function () {
        if (!(liveStateSyncElement === null || liveStateSyncElement === void 0 ? void 0 : liveStateSyncElement.getEditorAccessTimer))
            return;
        var subscription = liveStateSyncElement.getEditorAccessTimer().subscribe(function (res) {
            setData(res);
        });
        return function () {
            subscription.unsubscribe();
        };
    }, [liveStateSyncElement === null || liveStateSyncElement === void 0 ? void 0 : liveStateSyncElement.getEditorAccessTimer]);
    return data;
}
export function useEditorAccessRequestHandler() {
    var liveStateSyncElement = useLiveStateSyncUtils();
    var _a = React.useState(null), data = _a[0], setData = _a[1];
    useEffect(function () {
        if (!(liveStateSyncElement === null || liveStateSyncElement === void 0 ? void 0 : liveStateSyncElement.isEditorAccessRequested))
            return;
        var subscription = liveStateSyncElement.isEditorAccessRequested().subscribe(function (res) {
            setData(res);
        });
        return function () {
            subscription.unsubscribe();
        };
    }, [liveStateSyncElement === null || liveStateSyncElement === void 0 ? void 0 : liveStateSyncElement.isEditorAccessRequested]);
    return data;
}
export function useServerConnectionStateChangeHandler() {
    var liveStateSyncElement = useLiveStateSyncUtils();
    var _a = React.useState(), data = _a[0], setData = _a[1];
    useEffect(function () {
        if (!(liveStateSyncElement === null || liveStateSyncElement === void 0 ? void 0 : liveStateSyncElement.onServerConnectionStateChange))
            return;
        var subscription = liveStateSyncElement.onServerConnectionStateChange().subscribe(function (res) {
            setData(res);
        });
        return function () {
            subscription.unsubscribe();
        };
    }, [liveStateSyncElement === null || liveStateSyncElement === void 0 ? void 0 : liveStateSyncElement.onServerConnectionStateChange]);
    return data;
}
export function useLiveState(liveStateDataId, initialValue, options) {
    var liveStateSyncElement = useLiveStateSyncUtils();
    var client = useVeltClient().client;
    var serverConnectionState = useServerConnectionStateChangeHandler();
    var _a = React.useState(null), documentId = _a[0], setDocumentId = _a[1];
    var _b = React.useState(null), user = _b[0], setUser = _b[1];
    var _c = React.useState(initialValue), data = _c[0], setData = _c[1];
    useEffect(function () {
        resetLiveStateData();
    }, [documentId, user]);
    var resetLiveStateData = function () {
        if (user && documentId) {
            liveStateSyncElement === null || liveStateSyncElement === void 0 ? void 0 : liveStateSyncElement.setLiveStateData(liveStateDataId, data);
        }
    };
    useEffect(function () {
        if (!(liveStateSyncElement === null || liveStateSyncElement === void 0 ? void 0 : liveStateSyncElement.getLiveStateData))
            return;
        var documentPathsSubscription, userSubscription;
        if (options === null || options === void 0 ? void 0 : options.resetLiveState) {
            documentPathsSubscription = client.docService.getDocumentPaths$().subscribe(function (paths) {
                if (paths) {
                    setDocumentId(paths === null || paths === void 0 ? void 0 : paths.documentId);
                    documentPathsSubscription === null || documentPathsSubscription === void 0 ? void 0 : documentPathsSubscription.unsubscribe();
                }
            });
            userSubscription = client.authService.getUser$().subscribe(function (user) {
                if (user) {
                    setUser(user);
                    userSubscription === null || userSubscription === void 0 ? void 0 : userSubscription.unsubscribe();
                }
            });
        }
        var subscription = liveStateSyncElement.getLiveStateData(liveStateDataId, { listenToNewChangesOnly: options === null || options === void 0 ? void 0 : options.listenToNewChangesOnly }).subscribe(function (res) {
            setData(res);
        });
        return function () {
            subscription.unsubscribe();
            documentPathsSubscription === null || documentPathsSubscription === void 0 ? void 0 : documentPathsSubscription.unsubscribe();
            userSubscription === null || userSubscription === void 0 ? void 0 : userSubscription.unsubscribe();
        };
    }, [liveStateSyncElement === null || liveStateSyncElement === void 0 ? void 0 : liveStateSyncElement.getLiveStateData]);
    var useDebounce = function (callback) {
        var callbackRef = useRef(callback);
        var timerRef = useRef(null);
        useEffect(function () {
            callbackRef.current = callback;
        });
        var debounce = function (value, delay) {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            timerRef.current = setTimeout(function () {
                callbackRef.current(value);
            }, delay);
        };
        return debounce;
    };
    var setDataFunction = function (value) {
        setData(value);
        debouncedOnInput(value, (options === null || options === void 0 ? void 0 : options.syncDuration) >= 0 ? options === null || options === void 0 ? void 0 : options.syncDuration : 50); // default 50ms debounce time
    };
    var debouncedOnInput = useDebounce(function (value) {
        if (liveStateSyncElement === null || liveStateSyncElement === void 0 ? void 0 : liveStateSyncElement.setLiveStateData) {
            liveStateSyncElement.setLiveStateData(liveStateDataId, value);
        }
    });
    return [data, setDataFunction, serverConnectionState];
}
